<script setup lang="ts">
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'

export type FilterType = 'itemCondition' | 'status' | 'marketplace'

const props = defineProps({
	filterContent: {
		type: Object,
		required: true,
	},
	filterInstance: {
		type: Object,
		required: true,
	},
	isBottomSheet: {
		type: Boolean,
		default: false,
	},
})

const { isBottomSheet, filterInstance } = props

const emit = defineEmits(['checkboxChange', 'categorySelect'])

const handleCheckbox = (type: FilterType, id: string, checked: boolean) => {
	emit('checkboxChange', { type, id, checked })
}

const handleCategory = (e: Event, levelIndex: number) => {
	emit('categorySelect', { e, levelIndex })
}
</script>

<template>
	<div
		:class="
			isBottomSheet
				? 'border-b border-n-200 pb-20px mdp:border-0 mdp:pb-0 '
				: 'pb-0px'
		"
	>
		<h4
			:class="
				isBottomSheet
					? 'typography-b2-str mb-16px text-n-950'
					: 'typography-b3-reg mb-16px text-n-950'
			"
		>
			{{ filterContent.category.title }}
		</h4>
		<div :class="isBottomSheet ? '' : 'space-y-12px'">
			<div
				v-for="(level, levelIndex) in filterInstance.categoryLevels.value"
				:key="`category-level-${levelIndex}`"
				:class="isBottomSheet ? 'mb-16px' : 'mb-12px'"
				class="relative"
			>
				<select
					:id="`level-select-${levelIndex}`"
					:name="`level-level-${levelIndex}`"
					:value="level.selectedId"
					@change="e => handleCategory(e, levelIndex)"
					class="typography-s1-reg pr-40px w-full cursor-pointer appearance-none rounded-lg border border-n-200 bg-white p-12px text-n-950 outline-none hover:border-o-200 focus:border-o-400 focus:ring-1 focus:ring-o-200"
				>
					<option
						v-for="option in level.options"
						:key="`${levelIndex}-${option.id}`"
						:value="option.id"
					>
						{{ option.label }}
					</option>
				</select>
				<BaseIcon
					name="chevron_thin"
					class="h-20 w-20 pointer-events-none absolute right-12px top-1/2 -translate-y-1/2 rotate-90 transform bg-white fill-n-950"
				/>
			</div>
		</div>
	</div>

	<div :class="isBottomSheet ? 'border-b border-n-200 py-20px' : 'py-24px'">
		<h4
			:class="
				isBottomSheet
					? 'typography-b2-str mb-16px text-n-950'
					: 'typography-b3-reg mb-16px text-n-950'
			"
		>
			{{ filterContent.itemCondition.title }}
		</h4>
		<div :class="isBottomSheet ? 'space-y-16px' : 'flex flex-col gap-16px'">
			<label
				v-for="option in filterContent.itemCondition.options"
				:key="option.id"
				:class="
					isBottomSheet
						? 'flex cursor-pointer items-center justify-between'
						: 'flex cursor-pointer items-center gap-12px'
				"
			>
				<template v-if="isBottomSheet">
					<span class="typography-s1-reg text-n-950">
						{{ option.label }}
					</span>
					<slot
						name="checkbox"
						:option="option"
						:type="'itemCondition' as FilterType"
						:checked="
							filterInstance.tempFilterState.itemCondition.includes(option.id)
						"
						:on-change="
							(checked: boolean) =>
								handleCheckbox('itemCondition', option.id, checked)
						"
					/>
				</template>
				<template v-else>
					<slot
						name="checkbox"
						:option="option"
						:type="'itemCondition' as FilterType"
						:checked="
							filterInstance.tempFilterState.itemCondition.includes(option.id)
						"
						:on-change="
							(checked: boolean) =>
								handleCheckbox('itemCondition', option.id, checked)
						"
					/>
					<span class="typography-s1-reg text-n-950">
						{{ option.label }}
					</span>
				</template>
			</label>
		</div>
	</div>

	<div
		v-if="filterContent.status.options.length > 0"
		:class="isBottomSheet ? 'border-b border-n-200 py-20px' : 'py-24px'"
	>
		<h4
			:class="
				isBottomSheet
					? 'typography-b2-str mb-16px text-n-950'
					: 'typography-b3-reg mb-16px text-n-950'
			"
		>
			{{ filterContent.status.title }}
		</h4>
		<div :class="isBottomSheet ? 'space-y-16px' : 'flex flex-col gap-16px'">
			<label
				v-for="option in filterContent.status.options"
				:key="option.id"
				:class="
					isBottomSheet
						? 'flex cursor-pointer items-center justify-between'
						: 'flex cursor-pointer items-center gap-12px'
				"
			>
				<template v-if="isBottomSheet">
					<span class="typography-s1-reg text-n-950">
						{{ option.label }}
					</span>
					<slot
						name="checkbox"
						:option="option"
						:type="'status' as FilterType"
						:checked="filterInstance.tempFilterState.status.includes(option.id)"
						:on-change="
							(checked: boolean) => handleCheckbox('status', option.id, checked)
						"
					/>
				</template>
				<template v-else>
					<slot
						name="checkbox"
						:option="option"
						:type="'status' as FilterType"
						:checked="filterInstance.tempFilterState.status.includes(option.id)"
						:on-change="
							(checked: boolean) => handleCheckbox('status', option.id, checked)
						"
					/>
					<span class="typography-s1-reg text-n-950">
						{{ option.label }}
					</span>
				</template>
			</label>
		</div>
	</div>

	<slot name="price"></slot>

	<div :class="isBottomSheet ? 'py-20px' : 'py-24px'">
		<h4
			:class="
				isBottomSheet
					? 'typography-b2-str mb-16px text-n-950'
					: 'typography-b3-reg mb-16px text-n-950'
			"
		>
			{{ filterContent.marketplace.title }}
		</h4>
		<div :class="isBottomSheet ? 'space-y-16px' : 'flex flex-col gap-16px'">
			<label
				v-for="option in filterContent.marketplace.options"
				:key="option.id"
				:class="
					isBottomSheet
						? 'flex cursor-pointer items-center justify-between'
						: 'flex cursor-pointer items-center gap-12px'
				"
			>
				<template v-if="isBottomSheet">
					<span class="typography-s1-reg text-n-950">
						{{ option.label }}
					</span>
					<slot
						name="checkbox"
						:option="option"
						:type="'marketplace' as FilterType"
						:checked="
							filterInstance.tempFilterState.marketplace.includes(option.id)
						"
						:on-change="
							(checked: boolean) =>
								handleCheckbox('marketplace', option.id, checked)
						"
					/>
				</template>
				<template v-else>
					<slot
						name="checkbox"
						:option="option"
						:type="'marketplace' as FilterType"
						:checked="
							filterInstance.tempFilterState.marketplace.includes(option.id)
						"
						:on-change="
							(checked: boolean) =>
								handleCheckbox('marketplace', option.id, checked)
						"
					/>
					<span class="typography-s1-reg text-n-950">
						{{ option.label }}
					</span>
				</template>
			</label>
		</div>
	</div>
</template>
